import { Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsClientAssetModel } from 'src/api/generated';
import { sanitizeFileName, sanitizeUrl } from 'src/utils/sanitize';

import {
  AssetListGrid,
  AssetMeta,
  AssetName,
  AssetRow,
  AssetRowText,
  AssetThumbnail,
  EmptyStateWrapper,
} from './AssetPicker.styled';
import { useAssetDimensions } from './useAssetDimensions';
import { isPreviewableAsset } from './utils';

interface AssetSelectRowProps {
  asset: FanguardApiContractsClientAssetModel;
  isSelected: boolean;
  onSelect: (assetId: string) => void;
}

const AssetSelectRow = ({ asset, isSelected, onSelect }: AssetSelectRowProps) => {
  const isPreviewable = isPreviewableAsset(asset);
  const previewUrl = sanitizeUrl(asset.url || '');
  const dimensions = useAssetDimensions(previewUrl, isPreviewable);

  return (
    <AssetRow
      $isSelected={isSelected}
      onClick={() => onSelect(asset.id || '')}
      data-testid={`asset-picker-row-${asset.id}`}
    >
      <AssetThumbnail $preview={isPreviewable ? previewUrl : undefined} />
      <AssetRowText>
        <AssetName title={sanitizeFileName(asset.name || '')}>{sanitizeFileName(asset.name || '')}</AssetName>
        {dimensions && <AssetMeta>{`${dimensions.width} x ${dimensions.height} px`}</AssetMeta>}
      </AssetRowText>
      <Checkbox checked={isSelected} data-testid={`asset-picker-checkbox-${asset.id}`} />
    </AssetRow>
  );
};

interface AssetSelectListProps {
  assets: FanguardApiContractsClientAssetModel[];
  selectedAssetId: string | null;
  onSelect: (assetId: string) => void;
  isDesktop: boolean;
}

export const AssetSelectList = ({ assets, selectedAssetId, onSelect, isDesktop }: AssetSelectListProps) => {
  const { t } = useTranslation();

  if (!assets.length) {
    return <EmptyStateWrapper>{t('assets.noAssetsAvailable')}</EmptyStateWrapper>;
  }

  return (
    <AssetListGrid $isDesktop={isDesktop} data-testid="asset-picker-list">
      {assets.map((asset) => (
        <AssetSelectRow key={asset.id} asset={asset} isSelected={selectedAssetId === asset.id} onSelect={onSelect} />
      ))}
    </AssetListGrid>
  );
};
